import { ResourceClient, type HttpClient, type JobDTO } from "@dl/web-core-js/api";

/**
 * The native manufacturing master — the SIGM MFI replacement.
 *
 * Not to be confused with `api/part.ts`, which reads SIGM's `mfi` database
 * directly and stays until the pipeline reads from here instead. Wire names
 * match the Java `@JsonProperty`s exactly.
 */

/* ----------------------------------------------------------------- types --- */

/** VIRTUAL: a virtual panel (substrate, thickness, finish), consumed by area and cut from a real panel. */
export const PART_TYPES = ["PURCHASED", "MANUFACTURED", "VIRTUAL"] as const;
export type PartType = (typeof PART_TYPES)[number];

export const PART_STATUSES = ["ACTIVE", "INACTIVE"] as const;
export type PartStatus = (typeof PART_STATUSES)[number];

/**
 * TRACKED: receptions and production output add, usage subtracts, counted.
 * UNTRACKED: never held as stock; expensed when received (0061).
 */
export const INVENTORY_POLICIES = ["TRACKED", "UNTRACKED"] as const;
export type InventoryPolicy = (typeof INVENTORY_POLICIES)[number];

export const PART_EDGES = ["TOP", "RIGHT", "BOTTOM", "LEFT"] as const;
export type PartEdge = (typeof PART_EDGES)[number];

export const PART_UNIT_ROLES = ["PURCHASE", "SALE", "PACKAGE"] as const;
export type PartUnitRole = (typeof PART_UNIT_ROLES)[number];

/** Exactly the database's `currency` type. EUR was listed here once and failed on save. */
export const CURRENCIES = ["CAD", "USD"] as const;
export type Currency = (typeof CURRENCIES)[number];

/** One row of the parts grid: group, unit, department and size pre-resolved. */
export interface PartGridDTO {
  id: number;
  number: string;
  name: string;
  description: string | null;
  type: PartType;
  status: PartStatus;
  inventory_policy: InventoryPolicy;
  part_group_id: number | null;
  part_group_code: string | null;
  part_group_name: string | null;
  stock_unit_code: string | null;
  department_code: string | null;
  drawing_number: string | null;
  lead_time_days: number | null;
  /** Millimetres and grams — the base units, so mixed entries sort together. */
  length_mm: number | null;
  width_mm: number | null;
  height_mm: number | null;
  weight_g: number | null;
  supplier_count: number;
  /**
   * The newest revision's status, or null where the part has none. 13,169 of
   * 27,298 have none, 5,439 of those marked as made in-house.
   */
  bom_status: BomStatus | null;
  bom_line_count: number;
  /** On hand across every plant warehouse, from the stock ledger. Zero when it has never moved. */
  on_hand: number;
}

/** The part record itself. This is what PUT writes. */
export interface PartDTO {
  id: number;
  number: string;
  name: string;
  description: string | null;
  type: PartType;
  status: PartStatus;
  part_group_id: number | null;
  stock_unit_id: number;
  department_id: number | null;
  inventory_policy: InventoryPolicy;
  harmonized_code: string | null;
  customs_description: string | null;
  upc: string | null;
  /** The drawing and CNC program the part is made by (3411X). */
  drawing_number: string | null;
  /**
   * Where this part is used up — VIZU for the paper it laminates onto board.
   * Null means wherever the production order runs. Never a distribution
   * warehouse. Purchased parts are planned and received here.
   */
  consumption_warehouse_id: number | null;
  production_multiple: number | null;
  hole_count: number | null;
  created_date: string | null;
  inactivated_date: string | null;
  /** The letter a component goes by on the floor and in the assembly guide (G1, X1). */
  letter?: string | null;
  /** Nested per cutboard: Cut and Edge count boards, ceil(quantity / this). Null or 1: one per board. */
  parts_per_cutboard?: number | null;
  /** Most per pallet: a lot prints one drawing per pallet. Null: one pallet. */
  pallet_quantity?: number | null;
  /** How a parent gets this part, over its group's default. Null: the group's. */
  supply?: BomSupply | null;
  /** Consumed in whole units by a lot (a panel started is a panel used): consumption rounds up. */
  is_consumed_whole?: boolean;
  /** Made in steps of its own: its BOM page shows the routing (0072). Only a made part has one. */
  has_routing?: boolean;
  /** Finished products: the letter of the component that carries the customer-service label. */
  service_label_letter?: string | null;
  /** Derived server-side, read-only. */
  is_purchased?: boolean;
  is_stocked?: boolean;
}

/** A remark on a part, with who wrote it and when (0063). Only its author may correct or delete it. */
export interface PartNoteDTO {
  id: number;
  part_id: number;
  body: string;
  /** Read only: set from the signed-in account. */
  created_by_account_id?: number | null;
  created_by_username?: string | null;
  created_at?: string;
  updated_at?: string;
  /** Read only: the signed-in person wrote it. */
  is_mine?: boolean;
}

export class PartNoteClient extends ResourceClient<PartNoteDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/part-note");
  }

  /** A part's notes, oldest first. */
  async ofPart(partId: number): Promise<PartNoteDTO[]> {
    return (await this.find({ page: 0, size: 1000, filters: { part_id: [String(partId)] } })).content;
  }
}

export interface PartMeasurementDTO {
  id: number;
  part_id: number;
  measurement_kind_id: number;
  value: number;
  unit_id: number;
  /** Trigger-maintained; never sent. */
  value_base?: number | null;
}

export interface PartEdgebandDTO {
  id: number;
  part_id: number;
  edge: PartEdge;
  material_part_id: number;
  /** Banded on part of the side only (SIGM 1.5L): half a side in the pattern. */
  is_partial: boolean;
  /** On a side banded in part: how much of it, in mm (0079). Null: half the side. */
  banded_mm?: number | null;
  /** Joined for display; never sent. */
  material_part_number?: string | null;
}

export interface PartUnitDTO {
  id: number;
  part_id: number;
  unit_id: number;
  role: PartUnitRole;
  /** How many stock units this alternative unit holds. */
  quantity_in_stock_unit: number;
  supplier_id: number | null;
}

export interface PartSupplierDTO {
  id: number;
  part_id: number;
  supplier_id: number;
  priority: number;
  supplier_part_number: string | null;
  price: number | null;
  currency: Currency;
  unit_id: number | null;
  price_date: string | null;
  /** Overrides the supplier's lead time for this part only. */
  lead_time_days: number | null;
  /** The least the supplier sells, in stock units. */
  minimum_order_quantity: number | null;
  /** Sold only in multiples of this — a bundle of 20 sheets. A PER_PART order unit replaces it as the step. */
  order_multiple: number | null;
  /** What it is ordered in from this supplier: a barrel, a roll, its truck. Null: its stock unit. */
  order_unit_id: number | null;
  /** Read only. */
  order_unit_code?: string | null;
  order_unit_kind?: "PER_PART" | "SHARED" | null;
  /** PER_PART order unit: stock units one holds (a barrel of 208 L). */
  stock_units_per_order_unit: number | null;
  /** SHARED order unit: load units one stock unit takes in it. */
  load_per_stock_unit: number | null;
  /** PER_PART order unit: the fewest the supplier takes, in order units. */
  minimum_order_units: number | null;
  note: string | null;
  /** Read only, for links both ways. */
  part_number?: string | null;
  part_name?: string | null;
  supplier_code?: string | null;
  supplier_name?: string | null;
}

export interface PartAttributeValueDTO {
  id: number;
  part_id: number;
  attribute_id: number;
  value_text: string | null;
  value_number: number | null;
  value_number_base?: number | null;
  value_boolean: boolean | null;
  value_date: string | null;
  value_option_id: number | null;
  value_part_id: number | null;
  value_object_id: number | null;
}

export interface PartDetailDTO {
  part: PartDTO;
  measurements: PartMeasurementDTO[];
  edgebands: PartEdgebandDTO[];
  units: PartUnitDTO[];
  suppliers: PartSupplierDTO[];
  attribute_values: PartAttributeValueDTO[];
}

export interface SupplierDTO {
  id: number;
  code: string;
  name: string;
  legal_name: string | null;
  address_line: string | null;
  city: string | null;
  postal_code: string | null;
  country: string | null;
  phone: string | null;
  fax: string | null;
  website: string | null;
  currency: Currency;
  /** When this supplier is paid; drives the cash projection. Null only when creating: the purchasing default. */
  payment_terms: SupplierPaymentTerms | null;
  /** NET_DAYS only: days after receipt an invoice is paid. Null: the purchasing settings' default. */
  payment_terms_days: number | null;
  /** DEPOSIT only: percent paid when the order is sent; the rest on receipt. */
  deposit_percent: number | null;
  /** FIXED plans with lead_time_days; MEASURED with the median of its receipts, once there are enough. */
  lead_time_mode: SupplierLeadTimeMode;
  lead_time_days: number | null;
  /** Days of the lead time spent travelling once shipped; a closure does not hold these up. Null: all processing. */
  transit_days: number | null;
  /** Days between two orders to this supplier. Null: the settings' default. */
  order_cycle_days: number | null;
  /** 1 = Monday … 7 = Sunday: the day orders go out. */
  order_weekday: number | null;
  /** A subcontractor's own warehouse: what it consumes when its work is received. */
  subcontract_warehouse_id: number | null;
  note: string | null;
  is_active: boolean;
  created_date: string | null;
  /** Read only. Orders that went out: awaiting approval, issued or closed. */
  order_count?: number;
  /** Read only. Orders out and not closed: awaiting approval or issued. */
  open_order_count?: number;
  /** Read only. The latest order date among those that went out. */
  last_order_date?: string | null;
}

export const SUPPLIER_LEAD_TIME_MODES = ["FIXED", "MEASURED"] as const;
export const SUPPLIER_PAYMENT_TERMS = ["PAID_ON_ORDER", "PAID_ON_RECEIPT", "NET_DAYS", "DEPOSIT"] as const;
export type SupplierPaymentTerms = (typeof SUPPLIER_PAYMENT_TERMS)[number];
export type SupplierLeadTimeMode = (typeof SUPPLIER_LEAD_TIME_MODES)[number];

export interface SupplierContactDTO {
  id: number;
  supplier_id: number;
  name: string;
  role: string | null;
  phone: string | null;
  phone_extension: string | null;
  email: string | null;
  is_primary: boolean;
  position: number;
}

/* ------------------------------------------------------- reference types --- */

export type UnitDimension = "COUNT" | "LENGTH" | "MASS" | "VOLUME" | "AREA" | "TIME";

export interface UnitDTO {
  id: number;
  code: string;
  name: string;
  name_fr: string;
  dimension: UnitDimension;
  /** Null means the unit needs a per-part conversion — a box, a roll, a bag. */
  base_factor: number | null;
  decimals: number;
  is_active: boolean;
}

export interface MeasurementKindDTO {
  id: number;
  code: string;
  name: string;
  name_fr: string;
  dimension: UnitDimension;
  position: number;
  is_active: boolean;
}

/**
 * How a parent order gets a component (a part with a bill of its own):
 * PHANTOM consumes its materials directly (cut parts at Laval, hardware bags,
 * foam and cornières at their Terrebonne step), FROM_STOCK consumes it from
 * stock and stops there (kanban parts, the semi-finished set).
 */
export const BOM_SUPPLIES = ["PHANTOM", "FROM_STOCK"] as const;
export type BomSupply = (typeof BOM_SUPPLIES)[number];

export interface PartGroupDTO {
  id: number;
  code: string;
  name: string;
  /** What a new part in this group starts as. */
  default_inventory_policy: InventoryPolicy;
  is_active: boolean;
  /** How its purchased parts are replenished unless a part says otherwise. */
  default_replenishment_policy: ReplenishmentPolicy;
  /** Recorded (issued from stock) or derived (from finished work). */
  default_usage_source: UsageSource;
  /** Days of usage kept as safety stock. */
  safety_days: number;
  /** Added for parts that feed products the pipeline protects. */
  strategic_extra_days: number;
  /** MATERIAL follows the products that use it, FINISHED its own product, GENERAL is kept by hand. */
  kind: PartGroupKind;
  /** How a parent gets a part of this group unless the part or the BOM line says otherwise. */
  default_supply?: BomSupply;
}

export const PART_GROUP_KINDS = ["MATERIAL", "FINISHED", "GENERAL"] as const;
export type PartGroupKind = (typeof PART_GROUP_KINDS)[number];

/**
 * AUTO follows usage; FIXED keeps a typed minimum and maximum; ON_DEMAND buys
 * only what open production orders need; NOT_PLANNED is left to people.
 */
export const REPLENISHMENT_POLICIES = ["AUTO", "FIXED", "ON_DEMAND", "NOT_PLANNED"] as const;
export type ReplenishmentPolicy = (typeof REPLENISHMENT_POLICIES)[number];

export const USAGE_SOURCES = ["RECORDED", "DERIVED"] as const;
export type UsageSource = (typeof USAGE_SOURCES)[number];

export interface DepartmentDTO {
  id: number;
  code: string;
  name: string;
  name_fr: string;
  hourly_rate: number | null;
  position: number;
  is_active: boolean;
}

export const ATTRIBUTE_DATA_TYPES = [
  "TEXT",
  "NUMBER",
  "BOOLEAN",
  "DATE",
  "ENUM",
  "PART_REFERENCE",
  "DOCUMENT",
] as const;
export type AttributeDataType = (typeof ATTRIBUTE_DATA_TYPES)[number];

export type AttributeEntity = "PART" | "PRODUCTION_ORDER" | "SUPPLIER" | "PRODUCT";

export interface AttributeDTO {
  id: number;
  entity: AttributeEntity;
  code: string;
  name: string;
  name_fr: string;
  data_type: AttributeDataType;
  unit_id: number | null;
  /** Free-text section heading, so a long registry reads in blocks. */
  group_name: string | null;
  position: number;
  is_required: boolean;
  is_searchable: boolean;
  is_system: boolean;
  help_text: string | null;
  /**
   * Set once a field has earned a column. The value then lives there and this
   * attribute is history — see the tier rule in the schema README.
   */
  promoted_to_column: string | null;
}

export interface AttributeOptionDTO {
  id: number;
  attribute_id: number;
  code: string;
  label: string;
  label_fr: string;
  position: number;
  is_active: boolean;
}

/** Which attributes apply to which part group. Empty means "all groups". */
export interface PartAttributeGroupDTO {
  id: number;
  attribute_id: number;
  part_group_id: number;
}

/* ------------------------------------------------- bill of materials --- */

export const BOM_STATUSES = ["DRAFT", "ACTIVE", "ARCHIVED"] as const;
export type BomStatus = (typeof BOM_STATUSES)[number];

export const BOM_LINE_KINDS = ["COMPONENT", "TEXT"] as const;
export type BomLineKind = (typeof BOM_LINE_KINDS)[number];

/**
 * A revision's label: when it was made, to the minute, local time
 * (2026-11-03 14:32). Its number only orders it; a revision synced to many
 * parts at once carries the same label on each. "#3" when the time is unknown
 * (a deleted revision on an old order).
 */
export function revisionLabel(at: string | null | undefined, revision: number): string {
  const when = at ? new Date(at) : null;
  if (!when || Number.isNaN(when.getTime())) return `#${String(revision)}`;
  const two = (n: number) => String(n).padStart(2, "0");
  return `${String(when.getFullYear())}-${two(when.getMonth() + 1)}-${two(when.getDate())} ${two(when.getHours())}:${two(when.getMinutes())}`;
}

/** One revision of how a part is made. SIGM had no revisions at all. */
export interface BomDTO {
  id: number;
  part_id: number;
  /** Orders the part's revisions; the label is created_at (revisionLabel). */
  revision: number;
  status: BomStatus;
  /** What it is, where one helps ("with metal legs"); shown after the label. */
  name: string | null;
  date_from: string | null;
  date_to: string | null;
  note: string | null;
  /** How many of the part the line quantities are written for: SIGM wrote most BOMs for 100. */
  base_quantity: number;
  /** DRAFT until a person approves the routing; a lot is released only on APPROVED ones, which are frozen. Read only. */
  routing_status?: RoutingStatus;
  routing_approved_at?: string | null;
  /** When it was made: its label. Read only. */
  created_at?: string;
  routing_approved_by_account_id?: number | null;
  routing_approved_by_name?: string | null;
}

export type RoutingStatus = "DRAFT" | "APPROVED";

export interface BomLineDTO {
  id: number;
  bom_id: number;
  position: number;
  kind: BomLineKind;
  component_part_id: number | null;
  /** Joined for display; never sent. */
  component_part_number?: string | null;
  component_part_name?: string | null;
  quantity: number;
  unit_id: number | null;
  operation_id: number | null;
  scrap_percent: number;
  note: string | null;
  /** Where this line is used up, when not where the order runs. */
  consumption_warehouse_id: number | null;
  /** A virtual panel line: the real panel it is cut from, one of the virtual panel's options. */
  default_real_part_id?: number | null;
  /** Read only. */
  default_real_part_number?: string | null;
  /** How this parent gets this component, over the component's own setting. Null: the component's. */
  supply?: BomSupply | null;
  /** Read only: written by the engineering BOM build, and rewritten on every run. */
  is_generated?: boolean;
  /** Read only: the component's part type; VIRTUAL lines are in mm². */
  component_part_type?: PartType | null;
  /** Read only: its quantity comes from the part's sizes (a panel's area, a cornière's length, an edge band's length), never typed (0078). */
  is_sized?: boolean;
  /** Assembly BOMs: the box the line goes in (1, 2, 3). Null: not box-specific. */
  box_number?: number | null;
}

export interface BomOperationDTO {
  id: number;
  bom_id: number;
  position: number;
  operation_id: number;
  /** Null means the operation's own department. */
  department_id: number | null;
  station_id: number | null;
  is_setup: boolean;
  hours: number;
  crew_size: number;
  note: string | null;
}

/** One requirement from an explosion. Not stored — true only for the quantity asked. */
export interface BomExplosionLineDTO {
  level: number;
  part_id: number;
  part_number: string;
  part_name: string;
  part_type: PartType;
  parent_part_id: number;
  parent_part_number: string;
  quantity: number;
  unit_code: string | null;
  operation_code: string | null;
  has_children: boolean;
  path: string;
}

export interface BomWhereUsedDTO {
  bom_id: number;
  parent_part_id: number;
  parent_part_number: string;
  parent_part_name: string;
  revision: number;
  revision_at: string | null;
  status: BomStatus;
  quantity: number;
  unit_code: string | null;
  position: number;
}

export interface OperationDTO {
  id: number;
  code: string;
  name: string;
  name_fr: string;
  department_id: number | null;
  is_setup: boolean;
  is_indirect: boolean;
  is_printed_on_work_order: boolean;
  position: number;
  is_active: boolean;
}

/* -------------------------------------------------------- virtual panels --- */

export const PANEL_SUBSTRATES = ["PARTICLE", "MDF", "FIBREX", "PLYWOOD", "HOLLOW_BOARD"] as const;
export type PanelSubstrate = (typeof PANEL_SUBSTRATES)[number];

/** What a virtual part stands for, and so how its quantity becomes a real part's. */
/** PANEL by area, STRIP (cornières) by length, FOAM by area (0065). */
export type VirtualPartKind = "PANEL" | "STRIP" | "FOAM";

/**
 * What engineering BOMs consume when any of several real parts will do, of a
 * kind. A PANEL is a substrate, thickness and finish, consumed by area and cut
 * from one of several real panels; it is created here and numbered by the
 * server (V-PAR-16-BLANC), and only the yield and the default real part are
 * edited afterwards.
 */
export interface VirtualPartDTO {
  id: number;
  part_id: number;
  part_number?: string | null;
  part_name?: string | null;
  kind: VirtualPartKind;
  /** PANEL only. */
  substrate: PanelSubstrate | null;
  thickness_mm: number | null;
  finish: string | null;
  /** 0..1: the share of a sheet that ends up in parts (0.82). */
  expected_yield: number;
  default_real_part_id: number | null;
  default_real_part_number?: string | null;
  option_count?: number;
  line_count?: number;
}

/** A real part a virtual part can be consumed as: for a panel, a real panel it can be cut from. */
export interface VirtualPartOptionDTO {
  id: number;
  virtual_part_id: number;
  real_part_id: number;
  position: number;
  /** Real part's stock units per one unit of the virtual part, before the yield. Null: by the kind (a panel by sheet area). */
  conversion_factor?: number | null;
  real_part_number?: string | null;
  real_part_name?: string | null;
  real_part_status?: PartStatus | null;
  length_mm?: number | null;
  width_mm?: number | null;
}

export class VirtualPartClient extends ResourceClient<VirtualPartDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/virtual-part");
  }
}

export class VirtualPartOptionClient extends ResourceClient<VirtualPartOptionDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/virtual-part-option");
  }

  /** One virtual part's options, in order. */
  forVirtualPart(virtualPartId: number): Promise<VirtualPartOptionDTO[]> {
    return this.find({ page: 0, size: 1000, filters: { virtual_part_id: [String(virtualPartId)] } }).then((page) =>
      [...page.content].sort((a, b) => a.position - b.position),
    );
  }
}

/** What one run of the engineering BOM build did, or would do. */
export interface EngineeringBomReport {
  dry_run: boolean;
  counts: Record<string, number>;
  virtual_panels: Array<{
    number: string;
    name: string;
    expected_yield: number | null;
    default_real_number: string | null;
    options: string[];
    cut_part_count: number;
  }>;
  products_compared: number;
  products_within_25_percent: number;
  /** The products whose sheets per unit moved most, old wood BOM against new. */
  deviations: Array<{
    product_part_number: string;
    virtual_panel_number: string;
    old_sheets: number | null;
    new_sheets: number | null;
    ratio: number | null;
  }>;
  problem_counts: Record<string, number>;
  problems: string[];
  duration_ms: number;
}

/**
 * Builds the engineering BOMs (one unit, virtual panels by area) from SIGM's.
 * Synchronous: a dry run takes about 20 s and never refreshes from SIGM; a
 * real run with the refresh about four minutes.
 */
export class EngineeringBomClient {
  constructor(private readonly http: HttpClient) {}

  build(dryRun: boolean, refreshFromSigm: boolean): Promise<EngineeringBomReport> {
    return this.http.post<EngineeringBomReport>("/manufacturing/import/bom/engineering", {
      dry_run: dryRun,
      refresh_from_sigm: refreshFromSigm,
    });
  }
}

/** One run of the automatic part lifecycle: part status from product status. */
export interface PartLifecycleReport {
  dry_run: boolean;
  /** material_inactivated, material_activated, finished_inactivated, inactivated_with_stock, parts_considered... */
  counts: Record<string, number>;
  /** The first thousand changes. */
  changes: Array<{
    part_id: number;
    part_number: string;
    part_group_code: string | null;
    old_status: PartStatus;
    new_status: PartStatus;
    reason: string;
    has_stock: boolean;
  }>;
  duration_ms: number;
}

export class PartLifecycleClient {
  constructor(private readonly http: HttpClient) {}

  run(dryRun: boolean): Promise<PartLifecycleReport> {
    return this.http.post<PartLifecycleReport>(`/manufacturing/part-lifecycle/run?dry_run=${String(dryRun)}`, {});
  }
}

/** mm² as m², three decimals: the unit a person reads panel area in. */
export function formatSquareMetres(mm2: number | null | undefined): string {
  if (mm2 === null || mm2 === undefined) return "";
  return SQUARE_METRES.format(Number(mm2) / 1_000_000);
}
const SQUARE_METRES = new Intl.NumberFormat(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });

/* ------------------------------------------------------------------ stock --- */

export const STOCK_MOVEMENT_KINDS = [
  "RECEIPT",
  "ISSUE",
  "PRODUCTION_OUTPUT",
  "SHIPMENT",
  "TRANSFER",
  "ADJUSTMENT",
  "COUNT",
  "OPENING",
] as const;
export type StockMovementKind = (typeof STOCK_MOVEMENT_KINDS)[number];

export const STOCK_DOCUMENT_KINDS = [
  "RECEIPT",
  "REQUISITION",
  "PRODUCTION",
  "SHIPMENT",
  "TRANSFER",
  "ADJUSTMENT",
  "COUNT",
] as const;
export type StockDocumentKind = (typeof STOCK_DOCUMENT_KINDS)[number];

export type StockMovementSource = "NEXPIM" | "SIGM";

/** One line of the ledger. Never edited, never deleted — corrected by another movement. */
export interface StockMovementDTO {
  id: number;
  stock_document_id: number | null;
  part_id: number;
  part_number: string;
  part_name: string;
  warehouse_id: number;
  warehouse_code: string;
  kind: StockMovementKind;
  /** Signed, in the part's stock unit. Never zero. */
  quantity: number;
  /** The part's balance in this warehouse once this movement was posted (ledger order). Before = this minus quantity. */
  balance_after?: number | null;
  total_cost: number | null;
  movement_date: string;
  reverses_movement_id: number | null;
  reversed_by_movement_id: number | null;
  source: StockMovementSource;
  sigm_source_id: number | null;
  sigm_reference: string | null;
  document_kind: StockDocumentKind | null;
  reason_code: string | null;
  note: string | null;
  created_at: string;
}

export interface StockBalanceDTO {
  id: number;
  part_id: number;
  part_number: string;
  part_name: string;
  warehouse_id: number;
  warehouse_code: string;
  /** May be negative: a finding, not an error. */
  quantity: number;
  movement_count: number;
  last_movement_date: string | null;
  part_status: PartStatus;
  part_inactivated_date: string | null;
  part_group_code: string | null;
  part_group_name: string | null;
  part_group_kind: PartGroupKind | null;
  stock_unit_code: string | null;
}

export interface StockAdjustmentReasonDTO {
  id: number;
  code: string;
  name: string;
  name_fr: string;
  position: number;
  is_active: boolean;
}

export type StockAdjustmentMode = "DELTA" | "COUNTED";

export interface StockAdjustmentRequest {
  warehouse_id: number;
  stock_adjustment_reason_id: number;
  /** DELTA posts each quantity as given; COUNTED posts the difference from the balance at posting. */
  mode: StockAdjustmentMode;
  document_date: string | null;
  reference: string | null;
  note: string | null;
  lines: Array<{ part_id: number; quantity: number; note: string | null }>;
}

export interface StockAdjustmentResult {
  /** Null when a count found everything already right. */
  stock_document_id: number | null;
  movements_posted: number;
  lines_unchanged: number;
  lines: Array<{
    part_id: number;
    part_number: string;
    balance_before: number;
    delta: number;
    balance_after: number;
    stock_movement_id: number | null;
  }>;
}

export interface StockReconciliationReport {
  pairs_compared: number;
  matching: number;
  mismatched: number;
  lines: Array<{
    part_id: number | null;
    part_number: string;
    warehouse_code: string;
    ledger_quantity: number;
    sigm_quantity: number;
    difference: number;
  }>;
}

/* --------------------------------------------------------------- client --- */



/**
 * The part master. `detail()` is a read shape the server assembles; everything
 * else is the plain resource surface, which is where writes go. The list lives
 * at {@link PartGridClient}.
 */
export class ManufacturingPartClient extends ResourceClient<PartDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/part");
  }

  detail(id: number): Promise<PartDetailDTO> {
    return this.http.get<PartDetailDTO>(`${this.path}/${String(id)}/detail`);
  }
}

/** How a product is boxed for one sellable SKU (0066): CA sells as the product, US as its U SKU. */
export interface AssemblyVersionDTO {
  id: number;
  product_id: number;
  code: string;
  /** The sellable product it was in SIGM's model (255488U), until cutover; null for a version made here. */
  output_product_id: number | null;
  output_part_id: number;
  is_default: boolean;
  note: string | null;
  /** The order its rule is tried in, the default first. */
  position?: number;
  /** What a 3PL knows it by (255488U); empty: the product's SKU. */
  ship_sku?: string | null;
  /** Its rule: when it is used. */
  version_profile_id?: number | null;
  /** Read only. */
  version_profile_name?: string | null;
  product_sku?: string | null;
  output_sku?: string | null;
  output_part_number?: string | null;
  output_part_name?: string | null;
  /** Its part's status: ACTIVE is an active version. */
  part_status?: string | null;
  box_count?: number;
  /** Every piece of the semi-finished in a box, exactly as made (0069). */
  is_complete?: boolean;
  pieces?: number;
  unplaced_pieces?: number;
  over_pieces?: number;
  foreign_pieces?: number;
}

/** A carton of a version (0069): its own SKU, UPC, size and weight. */
export interface AssemblyVersionBoxDTO {
  id: number;
  assembly_version_id: number;
  box_number: number;
  package_sku: string | null;
  package_upc: string | null;
  width_in_inches: number;
  height_in_inches: number;
  depth_in_inches: number;
  weight_in_lbs: number;
}

export class AssemblyVersionBoxClient extends ResourceClient<AssemblyVersionBoxDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/assembly-version-box");
  }
}

export const VERSION_RULE_DIMENSIONS = ["MARKET", "CLIENT"] as const;
export type VersionRuleDimension = (typeof VERSION_RULE_DIMENSIONS)[number];
export const MARKETS = ["CA", "US"] as const;

/**
 * When a version is used (0069): a rule set on the versions of many products.
 * Values of one dimension are alternatives; every dimension it names must hold.
 */
export interface VersionProfileDTO {
  id: number;
  name: string;
  /** STRICT: only its versions serve what it matches. PREFERRED: the default when they have none. */
  substitution: "STRICT" | "PREFERRED";
  is_active: boolean;
  note: string | null;
  conditions: Array<{ dimension: VersionRuleDimension; value: string }>;
  /** Read only: the versions that use it. */
  version_count?: number;
}

export class VersionProfileClient extends ResourceClient<VersionProfileDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/version-profile");
  }
}

/** A piece of the semi-finished and where a version packs it. */
export interface PackedPieceDTO {
  part_id: number;
  number: string;
  name: string;
  unit_code: string | null;
  /** How many the semi-finished has, per unit; 0 for a piece it does not have. */
  made: number;
  /** Box number → how many go in it. */
  boxes: Record<string, number>;
}

export interface PackingDTO {
  assembly_version_id: number;
  box_count: number;
  /** One box and no list: everything goes in it. */
  implicit: boolean;
  pieces: PackedPieceDTO[];
}

export interface VersionResolutionDTO {
  version_id: number;
  code: string;
  part_id: number;
  is_default: boolean;
  ship_sku: string | null;
  profile_name: string | null;
  substitution: string | null;
  fallback_version_id: number | null;
  reason: string;
}

export interface VersionDemandDTO {
  version_id: number;
  code: string;
  units: number;
  clients: Array<{ client_id: number | null; client_no: string | null; client_name: string | null; market: string | null; units: number }>;
}

export interface VersionBoxStockDTO {
  assembly_version_id: number;
  code: string;
  part_number: string;
  warehouse_id: number;
  warehouse_code: string;
  units: number;
  loose_boxes: number;
  boxes: Record<string, number>;
}

export interface ProductVersionStockDTO {
  versions: VersionBoxStockDTO[];
  /** What the sales system says per SKU: the product's inventory until cutover. */
  sales_system: Array<{ warehouse_id: number; warehouse_code: string; quantity: number; reserved_quantity: number }>;
}

export interface StructureCandidateDTO {
  id: number;
  number: string;
  name: string;
  status: string;
}

/** A product's structure: its semi-finished (what Laval makes) and its assembly versions, the default first. */
export interface ProductStructure {
  product_id: number;
  sku: string;
  semi_finished_part_id: number | null;
  semi_finished_number: string | null;
  versions: AssemblyVersionDTO[];
}

export class AssemblyVersionClient extends ResourceClient<AssemblyVersionDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/assembly-version");
  }

  /** A product's structure; a version's SKU gives its base product's. */
  ofProduct(productId: number): Promise<ProductStructure> {
    return this.http.get<ProductStructure>(`${this.path}/structure/product/${String(productId)}`);
  }

  makeDefault(id: number): Promise<void> {
    return this.http.put<void>(`${this.path}/${String(id)}/default`, {}, { responseType: "void" });
  }

  /** On or off: its part's status. */
  setActive(id: number, active: boolean): Promise<void> {
    return this.http.put<void>(`${this.path}/${String(id)}/active`, { active }, { responseType: "void" });
  }

  /** The order the versions' rules are tried in; the default stays first. */
  reorder(productId: number, ids: number[]): Promise<void> {
    return this.http.put<void>(`${this.path}/product/${String(productId)}/order`, ids, { responseType: "void" });
  }

  setProfile(id: number, profileId: number | null): Promise<void> {
    return this.http.put<void>(`${this.path}/${String(id)}/profile`, { version_profile_id: profileId }, { responseType: "void" });
  }

  setShipSku(id: number, shipSku: string | null): Promise<void> {
    return this.http.put<void>(`${this.path}/${String(id)}/ship-sku`, { ship_sku: shipSku }, { responseType: "void" });
  }

  /** A new version: of a part given, else a copy of the default (BOM, boxes, packing list), off until switched on. */
  addVersion(productId: number, code: string, partId: number | null): Promise<AssemblyVersionDTO> {
    return this.http.post<AssemblyVersionDTO>(`${this.path}/product/${String(productId)}`, { code, part_id: partId });
  }

  removeVersion(id: number): Promise<void> {
    return this.http.post<void>(`${this.path}/${String(id)}/remove`, {}, { responseType: "void" });
  }

  setSemiFinished(productId: number, partId: number): Promise<void> {
    return this.http.put<void>(`${this.path}/product/${String(productId)}/semi-finished`, { part_id: partId }, { responseType: "void" });
  }

  /** Parts of a group with that role, not linked to any product yet. */
  candidates(role: "SEMI_FINISHED" | "ASSEMBLY_VERSION", q: string): Promise<StructureCandidateDTO[]> {
    return this.http.get<StructureCandidateDTO[]>(`${this.path}/candidates`, { query: { role, q } });
  }

  packing(id: number): Promise<PackingDTO> {
    return this.http.get<PackingDTO>(`${this.path}/${String(id)}/packing`);
  }

  /** Exclusive per unit: a piece's boxes never take more than the semi-finished has. */
  replacePacking(id: number, rows: Array<{ part_id: number; box_number: number; quantity: number }>): Promise<PackingDTO> {
    return this.http.put<PackingDTO>(`${this.path}/${String(id)}/packing`, rows);
  }

  /** Which version serves a market, a client or a sales channel; null when the product has none. */
  async resolve(productId: number, context: { market?: string; clientId?: number | null; salesChannelId?: number | null }): Promise<VersionResolutionDTO | null> {
    try {
      return await this.http.get<VersionResolutionDTO>(`${this.path}/resolve`, {
        query: {
          product_id: String(productId),
          market: context.market || undefined,
          client_id: context.clientId == null ? undefined : String(context.clientId),
          sales_channel_id: context.salesChannelId == null ? undefined : String(context.salesChannelId),
        },
      });
    } catch (failure) {
      if (failure instanceof Object && "status" in failure && (failure as { status: number }).status === 404) return null;
      throw failure;
    }
  }

  /** What each version would have served over the last weeks, by client. */
  preview(productId: number, weeks = 52): Promise<VersionDemandDTO[]> {
    return this.http.get<VersionDemandDTO[]>(`${this.path}/product/${String(productId)}/preview`, { query: { weeks: String(weeks) } });
  }

  stock(productId: number): Promise<ProductVersionStockDTO> {
    return this.http.get<ProductVersionStockDTO>(`${this.path}/product/${String(productId)}/stock`);
  }

  /** Sets how many of one box a warehouse has: an adjustment of the difference, with its reason. */
  countBox(id: number, body: { warehouse_id: number; box_number: number; counted: number; stock_adjustment_reason_id: number; note: string | null }): Promise<void> {
    return this.http.post<void>(`${this.path}/${String(id)}/stock/count`, body, { responseType: "void" });
  }

  /** The structure a part belongs to (a version's output or a semi-finished); null when none. */
  async ofPart(partId: number): Promise<ProductStructure | null> {
    try {
      return await this.http.get<ProductStructure>(`${this.path}/structure/part/${String(partId)}`);
    } catch (failure) {
      if (failure instanceof Object && "status" in failure && (failure as { status: number }).status === 404) return null;
      throw failure;
    }
  }
}

/**
 * Which box a piece of the semi-finished goes in, for one version (0068): a
 * line of the packer's list. A one-box version has none: everything goes in box 1.
 */
export interface AssemblyVersionPackingDTO {
  id: number;
  assembly_version_id: number;
  part_id: number;
  part_number?: string | null;
  part_name?: string | null;
  part_unit_code?: string | null;
  box_number: number;
  quantity: number;
  note: string | null;
}

export class AssemblyVersionPackingClient extends ResourceClient<AssemblyVersionPackingDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/assembly-version-packing");
  }
}

/** A CAD drawing or model on a part, stored privately; opened through a short-lived link. */
export const PART_FILE_KINDS = ["DRAWING", "CAD_SOURCE", "CNC_PROGRAM", "EXPLODED_VIEW", "ASSEMBLY_GUIDE", "PHOTO", "OTHER"] as const;
/**
 * What a part file is. DRAWING is the technical drawing the shop prints (on
 * the travellers); EXPLODED_VIEW the product's assembly picture on the
 * production sheet; ASSEMBLY_GUIDE the customer's instructions (FIS).
 */
export type PartFileKind = (typeof PART_FILE_KINDS)[number];

export interface PartFileDTO {
  id: number;
  /** The part it belongs to; null for a file of the product itself (0069). */
  part_id: number | null;
  /** The product it belongs to, for its own files (exploded view, general drawings). */
  product_id?: number | null;
  s3_object_id: number;
  file_name: string;
  content_type: string | null;
  file_size: number | null;
  note: string | null;
  uploaded_by_account_id: number | null;
  created_at: string | null;
  kind: PartFileKind;
  /** As the design office names it (A, B, R2). */
  revision: string | null;
  /** The one in use. A part has at most one current DRAWING and one current EXPLODED_VIEW. */
  is_current: boolean;
  /** The bill-of-materials revision it belongs to, when tied to one. */
  bom_id: number | null;
}

export class PartFileClient extends ResourceClient<PartFileDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/part-file");
  }

  /**
   * The endpoint reads the raw body, so the file goes up as octet-stream. The
   * kind defaults server-side to DRAWING for an image or a PDF; a new DRAWING
   * or EXPLODED_VIEW becomes the current one.
   */
  /** A file of the product itself (0069): its exploded view, its general drawings. */
  uploadToProduct(productId: number, file: File, options: { kind?: PartFileKind; revision?: string | null } = {}): Promise<PartFileDTO> {
    return this.http.request<PartFileDTO>(`${this.path}/upload`, {
      method: "POST",
      raw: file,
      query: { product_id: String(productId), filename: file.name, kind: options.kind, revision: options.revision ?? undefined },
    });
  }

  upload(partId: number, file: File, options: { kind?: PartFileKind; revision?: string | null; bomId?: number | null } = {}): Promise<PartFileDTO> {
    return this.http.request<PartFileDTO>(`${this.path}/upload`, {
      method: "POST",
      raw: file,
      query: {
        part_id: String(partId),
        filename: file.name,
        kind: options.kind,
        revision: options.revision ?? undefined,
        bom_id: options.bomId ?? undefined,
      },
    });
  }

  /** Makes a file the one in use; the previous current DRAWING or EXPLODED_VIEW is retired. */
  makeCurrent(id: number): Promise<PartFileDTO> {
    return this.http.post<PartFileDTO>(`${this.path}/${String(id)}/make-current`, {});
  }

  /** A link valid a few minutes: PDFs and images open in the browser, other files download. */
  async link(id: number): Promise<string> {
    return (await this.http.get<{ url: string }>(`${this.path}/${String(id)}/link`)).url;
  }
}

export class PartMeasurementClient extends ResourceClient<PartMeasurementDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/part-measurement");
  }
}

export class PartEdgebandClient extends ResourceClient<PartEdgebandDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/part-edgeband");
  }
}

export class PartUnitClient extends ResourceClient<PartUnitDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/part-unit");
  }
}

export class PartSupplierClient extends ResourceClient<PartSupplierDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/part-supplier");
  }
}

export class PartAttributeValueClient extends ResourceClient<PartAttributeValueDTO> {
  constructor(http: HttpClient) {
    super(http, "/part-attribute-value");
  }
}

export class SupplierClient extends ResourceClient<SupplierDTO> {
  constructor(http: HttpClient) {
    super(http, "/supplier");
  }
}

/**
 * Bill-of-materials revisions, plus the two questions they exist to answer.
 *
 * `explode` and `whereUsed` are read shapes the server computes — a recursive
 * walk belongs in the database, not in a browser holding 67,812 lines.
 */
export class BomClient extends ResourceClient<BomDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/bom");
  }

  explode(partId: number, quantity: number): Promise<BomExplosionLineDTO[]> {
    return this.http.get<BomExplosionLineDTO[]>(`${this.path}/explode/${String(partId)}`, {
      query: { quantity: String(quantity) },
    });
  }

  /**
   * Makes this the revision production builds from, archiving whichever was.
   * One act with two effects, so a POST rather than a status edit — setting
   * the column directly hits the one-active-per-part index.
   */
  activate(id: number): Promise<BomDTO> {
    return this.http.post<BomDTO>(`${this.path}/${String(id)}/activate`, {});
  }

  /** A new DRAFT revision copied from this one: header, lines and steps. */
  copy(id: number): Promise<BomDTO> {
    return this.http.post<BomDTO>(`${this.path}/${String(id)}/copy`, {});
  }

  whereUsed(partId: number, includeInactive = false): Promise<BomWhereUsedDTO[]> {
    return this.http.get<BomWhereUsedDTO[]>(`${this.path}/where-used/${String(partId)}`, {
      query: { include_inactive: String(includeInactive) },
    });
  }
}

export class BomLineClient extends ResourceClient<BomLineDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/bom-line");
  }
}

export class BomOperationClient extends ResourceClient<BomOperationDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/bom-operation");
  }
}

/** What is done to a part on the floor: cut, edge, machine, paint, inspect, pack. */
export interface ProcessDTO {
  id: number;
  code: string;
  name: string;
  name_fr: string;
  department_id: number | null;
  site_id: number | null;
  /** Cut and Edge count cutboards for a nested part. */
  counting_unit: "PART" | "CUTBOARD";
  /** The step runs a machine program, printed as a barcode on the drawing. */
  has_program: boolean;
  /** False for what is done to the whole lot (packing). */
  is_routable: boolean;
  position: number;
  is_active: boolean;
  /**
   * Where it falls in the plant's order (Cut 10, Edge and Machining 20, Paint
   * 30, Inspection 40): a new routing step waits, by default, for every step of
   * the nearest lower stage; equal stages run in parallel.
   */
  stage: number;
  /** Done outside the plant (Paint): its steps count what was sent and what came back. */
  is_external: boolean;
  /** PIECE: one to-do row per piece of a lot. STOCK: works raw stock for one or more lots: its to-do list is cut jobs (0074). */
  works_on?: "PIECE" | "STOCK";
  /** AUTO: as the steps report progress. COUNTED: the same until the station counts. MANUAL: only what the operator confirms. */
  consumption?: "AUTO" | "COUNTED" | "MANUAL";
}

export class ProcessClient extends ResourceClient<ProcessDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/process");
  }
}

/** Where the company operates (Laval, Terrebonne). */
export interface SiteDTO {
  id: number;
  code: string;
  name: string;
  address_line: string | null;
  city: string | null;
  is_active: boolean;
}

export class SiteClient extends ResourceClient<SiteDTO> {
  constructor(http: HttpClient) {
    super(http, "/site");
  }
}

/** A machine or bench: it performs one process. */
export interface StationDTO {
  id: number;
  code: string;
  name: string;
  site_id: number | null;
  operation_id: number | null;
  process_id: number | null;
  legacy_code: string | null;
  is_active: boolean;
}

export class StationClient extends ResourceClient<StationDTO> {
  constructor(http: HttpClient) {
    super(http, "/station");
  }
}

/**
 * One process in a revision's routing, with the machines allowed (any one of
 * them; none means any machine of the process) and the program.
 */
export interface BomRoutingStepDTO {
  id: number;
  bom_id: number;
  position: number;
  process_id: number;
  process_code?: string | null;
  process_name?: string | null;
  /** Edge program on EDGE, CNC program on MACHINING. */
  program_code: string | null;
  setup_minutes: number | null;
  /** Per part. */
  run_minutes: number | null;
  note: string | null;
  /** Written by the seeding from SIGM's free text, not by a person yet. */
  is_derived: boolean;
  station_ids: number[];
  station_codes?: string | null;
  /** The process's stage (read only): equal stages run in parallel. */
  process_stage?: number | null;
  /** The process is done outside (read only). */
  is_external?: boolean;
}

export type RoutingDependencyRule = "FLOW" | "FINISH";

/**
 * What a routing step waits for: FLOW, no more than the other has passed (50
 * cut, at most 50 edged); FINISH, once the other is done. Edited while the
 * routing is a draft.
 */
export interface BomRoutingStepDependencyDTO {
  id: number;
  bom_routing_step_id: number;
  depends_on_bom_routing_step_id: number;
  rule: RoutingDependencyRule;
  bom_id?: number | null;
  depends_on_position?: number | null;
  depends_on_process_name?: string | null;
}

export class BomRoutingStepDependencyClient extends ResourceClient<BomRoutingStepDependencyDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/bom-routing-step-dependency");
  }

  forBom(bomId: number): Promise<BomRoutingStepDependencyDTO[]> {
    return this.find({ page: 0, size: 1000, filters: { bom_id: [String(bomId)] } }).then((page) => page.content);
  }
}

export type RoutingReviewFlag = "NO_ROUTING" | "INSPECTION_ONLY" | "UNKNOWN_MACHINE" | "NO_DEPENDENCY" | "NO_MACHINE";

/** A floor-tracked part's routing, as the review worklist shows it. */
export interface RoutingReviewRowDTO {
  part_id: number;
  part_number: string;
  part_name: string | null;
  /** Its ACTIVE revision; null when it has none. */
  bom_id: number | null;
  revision: number | null;
  revision_at: string | null;
  /** Null without a routing. */
  routing_status: RoutingStatus | null;
  /** "Coupe › Chant | Usinage › Inspection": stages in order, "|" side by side. */
  steps: string;
  step_count: number;
  /** Some steps were read from SIGM's free text and nobody edited them. */
  derived: boolean;
  /** On a lot in the last 24 months, or an open one: a part the plant makes. */
  recently_made: boolean;
  flags: RoutingReviewFlag[];
  unknown_tokens: string[];
}

export class BomRoutingStepClient extends ResourceClient<BomRoutingStepDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/bom-routing-step");
  }
}

/** A part's routing as its free text describes it, saved nowhere. */
export interface RoutingProposalDTO {
  part_id: number;
  steps: BomRoutingStepDTO[];
  /** Words of the machine list that named no machine. */
  unknown_tokens: string[];
}

export interface RoutingSeedReportDTO {
  dry_run: boolean;
  parts_considered: number;
  revisions_seeded: number;
  steps_written: number;
  skipped_with_routing: number;
  parts_without_revision: number;
  unknown_tokens: Record<string, number>;
  sample: string[];
}

/** Routings read from the parts' machine lists and edge patterns. */
export class RoutingClient {
  constructor(private readonly http: HttpClient) {}

  derived(partId: number): Promise<RoutingProposalDTO> {
    return this.http.get<RoutingProposalDTO>(`/manufacturing/routing/derived/${String(partId)}`);
  }

  /** Writes the derived routing into the ACTIVE revision, or a new routing-only ACTIVE revision. */
  create(partId: number): Promise<{ bom_id: number }> {
    return this.http.post<{ bom_id: number }>(`/manufacturing/routing/create/${String(partId)}`, {});
  }

  seed(dryRun: boolean): Promise<RoutingSeedReportDTO> {
    return this.http.post<RoutingSeedReportDTO>("/manufacturing/routing/seed", { dry_run: dryRun });
  }

  /** Checks the routing (400 with what is wrong) and approves it: lots can be released on it, and it is frozen. */
  approve(bomId: number): Promise<void> {
    return this.http.post<void>(`/manufacturing/routing/${String(bomId)}/approve`, {});
  }

  /** Approves each that passes its checks; answers the refused ones, by revision id, with why. */
  approveAll(bomIds: number[]): Promise<Record<string, string>> {
    return this.http.post<Record<string, string>>("/manufacturing/routing/approve", { bom_ids: bomIds });
  }

  /** Back to draft, to change it. Running lots keep what they were released with. */
  toDraft(bomId: number): Promise<void> {
    return this.http.post<void>(`/manufacturing/routing/${String(bomId)}/draft`, {});
  }

  /** A draft routing's dependencies back to the stages' defaults. */
  resetDependencies(bomId: number): Promise<void> {
    return this.http.post<void>(`/manufacturing/routing/${String(bomId)}/default-dependencies`, {});
  }

  /** Every floor-tracked part's routing, parts the plant makes first, drafts first. */
  review(): Promise<RoutingReviewRowDTO[]> {
    return this.http.get<RoutingReviewRowDTO[]>("/manufacturing/routing/review");
  }
}

/** Every SIGM import in order (parts, BOMs, routings, stock, purchasing, production, service desk), as one background job. */
export class SigmRefreshClient {
  constructor(private readonly http: HttpClient) {}

  run(): Promise<JobDTO> {
    return this.http.post<JobDTO>("/manufacturing/import/all", undefined, { query: { async: true } });
  }
}

/**
 * The parts list, paged in the database. Sorting, filtering and search are the
 * server's; the grid's column ids are the field names it publishes.
 */
export class PartGridClient extends ResourceClient<PartGridDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/part-grid");
  }
}

export class StockMovementClient extends ResourceClient<StockMovementDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/stock-movement");
  }
}

export class StockBalanceClient extends ResourceClient<StockBalanceDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/stock-balance");
  }
}

export class StockAdjustmentReasonClient extends ResourceClient<StockAdjustmentReasonDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/stock-adjustment-reason");
  }
}

/**
 * The verbs that write the ledger, and its two checks. Every write here is
 * additive: an adjustment posts movements, a reversal posts the opposite one.
 */
export class StockClient {
  constructor(private readonly http: HttpClient) {}

  adjust(request: StockAdjustmentRequest): Promise<StockAdjustmentResult> {
    return this.http.post<StockAdjustmentResult>("/manufacturing/stock/adjustment", request);
  }

  reverse(movementId: number, note: string | null, reasonId: number | null = null): Promise<StockAdjustmentResult> {
    return this.http.post<StockAdjustmentResult>(`/manufacturing/stock/movement/${String(movementId)}/reverse`, {
      note,
      stock_adjustment_reason_id: reasonId,
    });
  }

  /** Balances disagreeing with their own ledger. Always empty unless something is broken. */
  drift(): Promise<Array<Record<string, unknown>>> {
    return this.http.get<Array<Record<string, unknown>>>("/manufacturing/stock/drift");
  }

  /**
   * Pulls SIGM's transactions since the last import into the ledger. Additive
   * and rerunnable: it starts after the highest transaction already imported.
   */
  syncFromSigm(): Promise<{ movements_written: number; transactions_seen: number }> {
    return this.http.post<{ movements_written: number; transactions_seen: number }>(
      "/manufacturing/stock/import",
      { dry_run: false },
    );
  }

  reconcile(limit = 200): Promise<StockReconciliationReport> {
    return this.http.get<StockReconciliationReport>("/manufacturing/stock/reconcile", {
      query: { limit: String(limit) },
    });
  }
}

/**
 * The sentence a stock endpoint answered a refusal with, or a fallback.
 * These endpoints body their 400s and 409s as `{message}`; the rest of the API
 * does not yet, which is why this is read here rather than by the HTTP client.
 */
export function refusalMessage(failure: unknown, fallback: string): string {
  const body = (failure as { body?: unknown } | null)?.body;
  if (body && typeof body === "object" && "message" in body && typeof body.message === "string") {
    return body.message;
  }
  return failure instanceof Error ? failure.message : fallback;
}

export class SupplierContactClient extends ResourceClient<SupplierContactDTO> {
  constructor(http: HttpClient) {
    super(http, "/supplier-contact");
  }
}

/** Days a supplier takes no orders and ships nothing: Chinese New Year, construction holidays. */
export interface SupplierClosureDTO {
  id: number;
  supplier_id: number;
  start_date: string;
  end_date: string;
  reason: string;
  /** The same days every year, from the stored dates onwards. */
  is_yearly: boolean;
  /** Read only: the next time it applies (or the current span). Null: it is over. */
  next_start_date?: string | null;
  next_end_date?: string | null;
}

export class SupplierClosureClient extends ResourceClient<SupplierClosureDTO> {
  constructor(http: HttpClient) {
    super(http, "/supplier-closure");
  }
}

/* ---------------------------------------------------------------- shape --- */

/**
 * A part's size as one string — "1570 × 345 × 16 mm".
 *
 * Dimensions are held one row per kind, which is right for storage and
 * unreadable in a list, so the grid shows them collapsed. Trailing dimensions
 * that were never entered are dropped rather than printed as zero: a panel
 * with no recorded thickness is not a panel 0 mm thick.
 */
export function dimensionLabel(row: {
  length_mm: number | null;
  width_mm: number | null;
  height_mm: number | null;
}): string {
  const parts = [row.length_mm, row.width_mm, row.height_mm];
  while (parts.length > 0 && (parts[parts.length - 1] === null || parts[parts.length - 1] === undefined)) {
    parts.pop();
  }
  // A thickness alone is not a size: "? × ? × 3.2 mm" says nothing useful.
  if (parts.length === 0 || (parts[0] == null && parts[1] == null)) return "";
  return `${parts.map((value) => (value === null || value === undefined ? "?" : formatNumber(value))).join(" × ")} mm`;
}

/** Trims the trailing zeros a NUMERIC column brings back: 1570.0 → "1570". */
const NUMBER_FORMAT = new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 });

/** For display only, never for an input to parse back: grouped by the browser's locale (7,384 or 7 384), at most 4 decimals. */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return "";
  return NUMBER_FORMAT.format(Number(value));
}

/* ------------------------------------------------------------ stocking --- */

/** How a stocking point is replenished: bought or subcontracted, made on runs of its own, or moved from another point. */
export const STOCKING_SOURCES = ["BUY", "MAKE", "TRANSFER"] as const;
export type StockingSource = (typeof STOCKING_SOURCES)[number];

/** A part kept at a warehouse, and how it is replenished there. No point: the part is not kept there. */
export interface StockingPointDTO {
  id: number;
  part_id: number;
  part_number?: string | null;
  part_name?: string | null;
  warehouse_id: number;
  warehouse_code?: string | null;
  source: StockingSource;
  /** TRANSFER: the warehouse it is moved from. */
  transfer_from_warehouse_id: number | null;
  transfer_from_warehouse_code?: string | null;
  /** Over the part's and its group's. Null: theirs. */
  replenishment_policy: ReplenishmentPolicy | null;
  safety_days: number | null;
  fixed_minimum: number | null;
  fixed_maximum: number | null;
  /** Where orders draw the part; one per part. */
  is_consumption_point: boolean;
  /** Where in the warehouse: aisle, rack or bin. Count sheets go in this order. */
  bin: string | null;
  note: string | null;
  /** Read only: the balance there now. */
  quantity?: number | null;
}

export class StockingPointClient extends ResourceClient<StockingPointDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/stocking-point");
  }

  /** A part's points, its consumption point first. */
  async forPart(partId: number): Promise<StockingPointDTO[]> {
    return (await this.find({ page: 0, size: 200, filters: { part_id: [String(partId)] } })).content;
  }
}
